// web/src/screens/admin/cards_list.ts — Plan 11-06 Task 2.
//
// Renders the Cards panel inside games_edit.ts. Lives as its own file
// (not a route entry) for clarity. Plan 11-06 Task 3 adds an analogous
// renderTasksPanel inside cards_edit.ts via tasks_list.ts.
//
// All API-supplied text via textContent (T-11-09a).

import { adminGet, adminPost, AdminError } from "../../api/admin.js";
import { showAdminToast } from "./admin_shell.js";
import type { AdminCardDTO } from "../../types/api.js";

function navigateAdmin(path: string): void {
  history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function renderCardsPanel(parent: HTMLElement, gameID: string, gameShortCode: string): void {
  const wrap = document.createElement("div");
  wrap.setAttribute("style", "background: white; padding: 12px; border-radius: 6px; border: 1px solid #e5e7eb;");
  parent.appendChild(wrap);

  const toolbar = document.createElement("div");
  toolbar.setAttribute("style", "display: flex; gap: 8px; align-items: center; margin-bottom: 8px;");
  const newBtn = document.createElement("button");
  newBtn.type = "button";
  newBtn.textContent = "+ New Card";
  newBtn.setAttribute("style", "padding: 6px 12px; background: #2563eb; color: white; border: 0; border-radius: 4px; cursor: pointer; font-size: 13px;");
  toolbar.appendChild(newBtn);
  wrap.appendChild(toolbar);

  const status = document.createElement("p");
  status.textContent = "Loading cards…";
  status.setAttribute("style", "color: #6b7280; font-size: 13px; margin: 4px 0;");
  wrap.appendChild(status);

  const ul = document.createElement("ul");
  ul.setAttribute("style", "list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 4px;");
  wrap.appendChild(ul);

  function rowFor(c: AdminCardDTO): HTMLLIElement {
    const li = document.createElement("li");
    li.setAttribute("style", "display: flex; align-items: center; gap: 8px; padding: 6px 10px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px;");
    const a = document.createElement("a");
    a.textContent = c.short_code ?? "(no short_code)"; // textContent
    a.setAttribute("href", `/admin/games/${encodeURIComponent(gameShortCode)}/cards/${encodeURIComponent(c.short_code ?? "")}`);
    a.setAttribute("style", "color: #1d4ed8; text-decoration: none; font-weight: 500; flex: 1;");
    a.addEventListener("click", (e) => {
      e.preventDefault();
      const sc = c.short_code;
      if (!sc) return;
      navigateAdmin(`/admin/games/${encodeURIComponent(gameShortCode)}/cards/${encodeURIComponent(sc)}`);
    });
    li.appendChild(a);
    if (c.ordinal != null) {
      const ord = document.createElement("span");
      ord.textContent = `ord ${c.ordinal}`;
      ord.setAttribute("style", "font-size: 12px; color: #6b7280;");
      li.appendChild(ord);
    }
    return li;
  }

  async function load(): Promise<void> {
    try {
      const cards = await adminGet<AdminCardDTO[]>(
        `/api/admin/games/${encodeURIComponent(gameID)}/cards`,
      );
      ul.replaceChildren();
      if (cards.length === 0) {
        status.textContent = "No cards yet — create one with “+ New Card”.";
        return;
      }
      status.textContent = `${cards.length} card${cards.length === 1 ? "" : "s"}.`;
      for (const c of cards) ul.appendChild(rowFor(c));
    } catch (err) {
      status.textContent = `Failed to load cards: ${(err as Error).message}`;
    }
  }

  async function onNew(): Promise<void> {
    const sc = window.prompt("New card short_code:");
    if (!sc) return;
    try {
      const created = await adminPost<AdminCardDTO, AdminCardDTO>(
        `/api/admin/games/${encodeURIComponent(gameID)}/cards`,
        { short_code: sc },
      );
      showAdminToast(document.body, `Created ${created.short_code}`);
      navigateAdmin(`/admin/games/${encodeURIComponent(gameShortCode)}/cards/${encodeURIComponent(created.short_code ?? sc)}`);
    } catch (err) {
      if (err instanceof AdminError && err.status === 409) {
        showAdminToast(document.body, "short_code already exists in this game");
      } else {
        showAdminToast(document.body, `Create failed: ${(err as Error).message}`);
      }
    }
  }

  newBtn.addEventListener("click", onNew);
  void load();
}
